<!-- ── Tool activity pills ── -->
@if (msg.toolActivity?.length) {
  <app-tool-activity-pills [activities]="msg.toolActivity!"></app-tool-activity-pills>
}

<!-- ── Thinking indicator ── -->
@if (msg.isThinking && !msg.content) {
  <div class="thinking-dots" aria-label="Agent is thinking">
    <span></span><span></span><span></span>
  </div>
}

<!-- ── Contact picker ── -->
<!-- Suppressed during the add-participants flow: there `get_sf_contacts` is
     called only to record the account / pre-load guests, NOT to ask the user to
     pick a primary contact. Hide it while the meeting picker (multipleMeetings)
     OR the add-participants card (addParticipantsDetails) is up, so the contact
     picker never renders above the "select a meeting" wizard.
     On past messages the tiles stay visible but read-only (disabled via isPast)
     so history shows the contact tiles instead of the agent's raw text. -->
@if (msg.contactsList?.length && !msg.addParticipantsDetails && !msg.multipleMeetings?.length && !msg.bookingSlots?.length) {
  <app-contact-picker
    [contacts]="msg.contactsList!"
    [disabled]="isPast || isProcessing || !!msg.contactsSelected"
    (contactSelected)="onContactSelected($event)">
  </app-contact-picker>
}

<!-- ── Executive picker ── -->
@if (msg.executivesList?.length && (!isPast || !!msg.executivesSelected)) {
  <!-- Impact: every tile in the event timezone (from the select_executives event,
       else the booking picker's zone). Pulse Check: each tile in its exec's zone. -->
  <app-executive-picker
    [executives]="msg.executivesList!"
    [allExecutives]="msg.allExecutivesList"
    [disabled]="isProcessing || !!msg.executivesSelected"
    [eventTzIana]="isImpact ? (msg.executivesEventTzIana || msg.bookingEventTzIana) : undefined"
    (executiveSelected)="onExecutiveSelected($event)">
  </app-executive-picker>
}

<!-- ── Call objective selector ── -->
@if (msg.callObjectiveOptions?.length && (!isPast || !!msg.callObjectiveConfirmed)) {
  <app-call-objective-selector
    [options]="msg.callObjectiveOptions!"
    [confirmed]="!!msg.callObjectiveConfirmed"
    (optionSelected)="onCallObjective($event)">
  </app-call-objective-selector>
}

<!-- ── Guest wizard (booking flow) ── -->
@if (msg.additionalGuestContacts !== undefined && !msg.bookingSlots?.length && !msg.addParticipantsDetails) {
  <app-guest-wizard
    [contacts]="msg.additionalGuestContacts"
    [initialGuests]="msg.additionalGuestsAdded"
    mode="booking"
    [disabled]="isPast"
    (guestsConfirmed)="onGuestsConfirmed($event)"
    (cancelled)="onGuestsConfirmed([])">
  </app-guest-wizard>
}

<!-- ── Booking calendar (inline slot picker) ── -->
@if (msg.bookingSlots?.length && (!isPast || !!msg.bookingConfirmed)) {
  <app-booking-calendar
    [slots]="msg.bookingSlots!"
    [routeId]="msg.bookingRouteId ?? ''"
    [execEmail]="msg.bookingExecEmail ?? ''"
    [execName]="msg.bookingExecName ?? ''"
    [duration]="msg.bookingDuration ?? ''"
    [formData]="msg.bookingFormData"
    [confirmed]="!!msg.bookingConfirmed"
    [confirmedTime]="msg.bookingConfirmedTime"
    [disabled]="isPast"
    [isConfirming]="isProcessing && !!msg.bookingSlotSelected"
    [eventTzIana]="msg.bookingEventTzIana"
    [eventTzLabel]="msg.bookingEventTzLabel"
    [lockTimezone]="isImpact"
    (slotSelected)="onSlotSelected($event)">
  </app-booking-calendar>
}

<!-- ── Reschedule calendar (inline slot picker, reschedule mode) ── -->
@if (msg.rescheduleSlots?.length && (!isPast || !!msg.rescheduleSlotsConfirmed)) {
  <app-booking-calendar
    mode="reschedule"
    [slots]="msg.rescheduleSlots!"
    [execEmail]="msg.rescheduleSlotsExecEmail ?? ''"
    [execName]="msg.rescheduleSlotsExecName ?? ''"
    [duration]="msg.rescheduleSlotsDuration ?? ''"
    [confirmed]="!!msg.rescheduleSlotsConfirmed"
    [confirmedTime]="msg.rescheduleSlotsConfirmedTime"
    [disabled]="isPast"
    [isConfirming]="isProcessing && !!msg.rescheduleSlotSelected"
    [eventTzIana]="msg.rescheduleSlotsEventTzIana"
    [eventTzLabel]="msg.rescheduleSlotsEventTzLabel"
    [lockTimezone]="isImpact"
    (slotSelected)="onRescheduleSlotSelected($event)">
  </app-booking-calendar>
}

<!-- ── Booking confirmed (when there are no slots, e.g. after reset) ── -->
@if (msg.bookingConfirmed && !msg.bookingSlots?.length) {
  <div class="booking-confirmed-standalone">
    <i class="pi pi-check-circle" aria-hidden="true"></i>
    <div>
      <strong>Meeting booked</strong>
      @if (msg.bookingConfirmedTime) { <p>{{ confirmedTimeLabel(msg.bookingConfirmedTime, msg.bookingEventTzIana) }}</p> }
    </div>
  </div>
}

<!-- ── Legacy booking summary card (Chili Piper URL) ── -->
@if (msg.bookingUrl && msg.bookingSummary && !isPast) {
  <app-booking-summary-card
    [bookingUrl]="msg.bookingUrl"
    [summary]="msg.bookingSummary"
    [confirmed]="!!msg.bookingUserMarkedComplete"
    [recoveryPending]="!!msg.bookingRecoveryPending"
    [recoveryUsed]="!!msg.bookingRecoveryUsed"
    (bookingDone)="onBookingDone()"
    (bookingFailed)="onBookingFailed()"
    (retrySameExecutive)="onRetryExecutive()"
    (tryDifferentExecutive)="onDifferentExecutive()">
  </app-booking-summary-card>
}

<!-- ── Report card (prep doc) — opens in the report panel ── -->
@if (msg.artifact && !msg.isPulseCheckComplete) {
  <app-artifact-card [artifact]="msg.artifact" />
}

<!-- ── Post-booking follow-up ── -->
@if (msg.reportUrl && !msg.isPulseCheckComplete && !isPast) {
  <app-post-booking-followup
    [answer]="msg.postBookingAnswer"
    [disabled]="isProcessing"
    (answered)="onPostBookingAnswer($event)">
  </app-post-booking-followup>
}

<!-- ── Multi-meeting picker (reschedule/cancel/objective/add-participants) ── -->
@if (msg.multipleMeetings?.length && !isPast) {
  <app-meeting-picker
    [meetings]="msg.multipleMeetings!"
    [flow]="msg.multipleMeetingsFlow ?? ''"
    [disabled]="isProcessing || !!msg.meetingPicked"
    [eventTzIana]="isImpact ? msg.multipleMeetingsEventTzIana : undefined"
    [lockTimezone]="isImpact"
    (meetingSelected)="onMeetingSelected($event)">
  </app-meeting-picker>
}

<!-- ── Reschedule card ── -->
@if (msg.rescheduleUrl && msg.rescheduleDetails && (!isPast || !!msg.rescheduleConfirmed)) {
  <app-reschedule-card
    [rescheduleUrl]="msg.rescheduleUrl"
    [details]="msg.rescheduleDetails"
    [confirmed]="!!msg.rescheduleConfirmed"
    (rescheduled)="onRescheduled()">
  </app-reschedule-card>
}

<!-- ── Cancel card ── -->
@if (msg.cancelDetails && !msg.cancelDismissed && (!isPast || !!msg.cancelConfirmed)) {
  <app-cancel-card
    [details]="msg.cancelDetails"
    [confirmed]="!!msg.cancelConfirmed"
    [dismissed]="!!msg.cancelDismissed"
    (cancelConfirmed)="onCancelConfirmed($event)"
    (cancelDismissed)="onCancelDismissed()">
  </app-cancel-card>
}

<!-- ── Add participants card ── -->
@if (msg.addParticipantsDetails && !msg.addParticipantsDismissed && (!isPast || !!msg.addParticipantsConfirmed)) {
  <app-add-participants-card
    [details]="msg.addParticipantsDetails"
    [contacts]="msg.additionalGuestContacts"
    [confirmed]="!!msg.addParticipantsConfirmed"
    [addedParticipants]="msg.addParticipantsAdded"
    [dismissed]="!!msg.addParticipantsDismissed"
    (participantsConfirmed)="onParticipantsConfirmed($event)"
    (cancelled)="onParticipantsCancelled()">
  </app-add-participants-card>
}

<!-- ── Markdown text — hidden when choice UI is active ── -->
@if (msg.content && !msg.isPulseCheckComplete && !msg.reportUrl && !(hasChoiceUi && !msg.isStreaming)) {
  <div class="pcheck-response" [class.pcheck-response--collapsed]="msg.isResponseCollapsed === true"
    [innerHTML]="msg.content | markdown"></div>
}

<!-- ── Pulse check complete confirmation card ── -->
@if (msg.isPulseCheckComplete && !msg.isStreaming) {
  <app-confirmation-card [content]="msg.content"></app-confirmation-card>
}

<!-- ── Streaming confirmation (before done event) ── -->
@if (msg.isPulseCheckComplete && msg.isStreaming && msg.content) {
  <div class="pcheck-response" [innerHTML]="msg.content | markdown"></div>
}
